import { Box, Divider, flex, Group, IconFont, Popover, SegmentedControl, Text } from '@byecode/ui'
import type { Field } from '@lighthouse/core'
import { BACKGROUND_TYPE, DataSourceType, FilterMode, type TableColumn, type ViewField } from '@lighthouse/core'
import {
    btnTypeList,
    FilledInput,
    FillPickerPopoverDropdown,
    getAppointField,
    getViewColumns,
    ListItem4ByecodeUi,
    notDataSettingInnerTypes,
    PURE_ALLOW_ICON_TYPES,
    SelectMode,
    silentInputTypes,
    useAtomAction,
    useAtomData,
    ViewFieldDisplayConfigure,
    ViewFieldFilterConfigure,
    ViewSortConfigure
} from '@lighthouse/shared'
import { find, reduce } from 'rambda'
import React, { useCallback, useDeferredValue, useMemo, useRef } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import styled from 'styled-components'

import {
    availableFieldBlocksAtom,
    availableFilterBlocksAtom,
    blockHighlightAtom,
    lastPageOfStackAtom,
    pagePathParameterFamily,
    pageQueryParameterFamily,
    pageStatesParameterFamily,
    pageVariableSchemasFamily,
    selectedBlockCustomViewAtom
} from '@/atoms/page/state'
import { ICON_POSITION_CONTROL } from '@/components/BlockSettings/Button/constant'
import { EventList } from '@/components/EventList'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useColorSystemAction } from '@/hooks/useColorSystemAction'
import { useDataSource, useDataSourceList } from '@/hooks/useDataSource'

import * as CM from '../../../styles'
import FormSegmentedControl from '../../FormSegmentedControl'
import { IconControl } from '../../IconControl'
import { InputControl } from '../../InputControl'
import { ListItemMenu } from '../../ListItemMenu'
import { SwitchControl } from '../../SwitchControl'
import {
    ALIGN_OPTIONS,
    DIRECTION_OPTIONS,
    inputHeightOptions,
    relativeSelectFieldList,
    SHOW_MODE_OPTIONS,
    VIEW_DATASOURCE_OPTIONS
} from '../constants'
import { CalendarConfig, CalendarDataConfig } from '../Data/CalendarConfig'
import type { FieldInputFormSlice } from '../formSlice'
import type { BaseFieldInputSetting } from '../types'

interface StyleProps extends BaseFieldInputSetting {
    field: Field | undefined
}

const SCxRelationSelectContainer = styled.div`
    padding: 0 8px;
`

const SCxRightFill = styled.div`
    width: 180px;
    /* max-width: 80%; */
    ${flex}
    justify-content: flex-end;
    align-items: center;
    gap: 8px;
`
export const Style: React.FunctionComponent<StyleProps> = ({ field, mode, pointer, prefix }) => {
    const btnRef = useRef<HTMLDivElement>(null)

    const isNotParentSubForm = mode !== 'subForm'

    const { watch, control } = useFormContext<FieldInputFormSlice>()
    const [inputType, showTitle, relativePointer = '', relativeShowMode, showType, iconPosition, addButtonEnable, action] = watch([
        `${prefix}inputType`,
        `${prefix}showTitle`,
        `${prefix}relativeSelect.relativePointer`,
        `${prefix}relativeSelect.showMode`,
        `${prefix}relativeSelect.addButton.showType`,
        `${prefix}relativeSelect.addButton.iconPosition`,
        `${prefix}relativeSelect.addButton.enable`,
        `${prefix}relativeSelect.addButton.action`
    ])

    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const relatedDataSource = useDataSource(appId, envId, relativePointer)
    const pageId = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => s?.pageId || '', [])
    )
    const pageVariables = useAtomData(pageVariableSchemasFamily(pageId))
    const pagePathParameter = useAtomData(pagePathParameterFamily(pageId))
    const pageQueryParameter = useAtomData(pageQueryParameterFamily(pageId))
    const pageStateParameter = useAtomData(pageStatesParameterFamily(pageId))
    const { run: setBlockHighlight } = useAtomAction(blockHighlightAtom)

    const handleMouseEnter = useCallback(
        (id: string) => {
            setBlockHighlight([id])
            document.querySelector(`div[data-node-id="${id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        },
        [setBlockHighlight]
    )

    const handleMouseLeave = useCallback(() => {
        setBlockHighlight([])
    }, [setBlockHighlight])
    const { schema: relatedSchema, viewOptions: relatedViewOptions } = relatedDataSource ?? { schema: {} }

    const { tableProps: relatedTableProps = [] } = relatedViewOptions ?? {}

    const columns = getViewColumns({
        tableProps: relatedTableProps,
        schema: relatedSchema
    })

    const primaryField = useMemo(() => {
        if (!relatedDataSource) {
            return columns[0]?.fieldId
        }
        const field = getAppointField(relatedDataSource, 'ID')
        return field?.id || columns[0]?.fieldId
    }, [columns, relatedDataSource])

    const showRelativeSetting = useMemo(() => {
        if (inputType !== 'relativeSelect') {
            return false
        }
        return mode === 'field' || (field && !relativeSelectFieldList.has(field.type))
    }, [field, inputType, mode])

    const customViewDsId = useDeferredValue(
        useAtomData(
            selectedBlockCustomViewAtom,
            useCallback(s => s?.config.pointer, [])
        )
    )
    const availableFieldBlocksInfo = useDeferredValue(useAtomData(availableFieldBlocksAtom))
    const availableFilterBlocksInfo = useDeferredValue(useAtomData(availableFilterBlocksAtom))
    const colorSystemMethods = useColorSystemAction()

    return (
        <>
            {inputType !== 'calendar' && (
                <>
                    <Divider />
                    <Group label="输入框">
                        {isNotParentSubForm && (
                            <Controller
                                name={`${prefix}showTitle`}
                                render={({ field: { value, onChange } }) => (
                                    <SwitchControl
                                        checked={value}
                                        label="显示标题"
                                        onChange={e => {
                                            onChange?.(e.currentTarget.checked)
                                        }}
                                    />
                                )}
                            />
                        )}
                        {showTitle && (
                            <Controller name={`${prefix}title`} render={({ field }) => <InputControl label="标题" {...field} />} />
                        )}
                        {!silentInputTypes.has(inputType) && (
                            <Controller name={`${prefix}placeholder`} render={({ field }) => <InputControl label="占位符" {...field} />} />
                        )}
                        {inputType === 'text' && (
                            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                <Text>输入框高度</Text>
                                <div style={{ width: 180 }}>
                                    <Controller
                                        name={`${prefix}inputHeight`}
                                        control={control}
                                        render={({ field }) => (
                                            <SegmentedControl
                                                data={inputHeightOptions}
                                                fullWidth
                                                value={field.value}
                                                onChange={field.onChange}
                                            />
                                        )}
                                    />
                                </div>
                            </ListItem4ByecodeUi>
                        )}

                        {inputType === 'relativeSelect' && (
                            <>
                                {isNotParentSubForm && (
                                    <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                        <Text>显示方式</Text>
                                        <div style={{ width: 180 }}>
                                            <Controller
                                                name={`${prefix}relativeSelect.showMode`}
                                                control={control}
                                                render={({ field }) => (
                                                    <SegmentedControl
                                                        data={SHOW_MODE_OPTIONS}
                                                        fullWidth
                                                        value={field.value}
                                                        onChange={field.onChange}
                                                    />
                                                )}
                                            />
                                        </div>
                                    </ListItem4ByecodeUi>
                                )}
                                {relativeShowMode !== 'input' && (
                                    <>
                                        <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                            <Text>排列方式</Text>
                                            <div style={{ width: 180 }}>
                                                <Controller
                                                    name={`${prefix}relativeSelect.direction`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <SegmentedControl
                                                            data={DIRECTION_OPTIONS}
                                                            fullWidth
                                                            value={field.value}
                                                            onChange={field.onChange}
                                                        />
                                                    )}
                                                />
                                            </div>
                                        </ListItem4ByecodeUi>
                                        <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                            <Text>对齐</Text>
                                            <div style={{ width: 180 }}>
                                                <Controller
                                                    name={`${prefix}relativeSelect.align`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <SegmentedControl
                                                            data={ALIGN_OPTIONS}
                                                            fullWidth
                                                            value={field.value}
                                                            onChange={field.onChange}
                                                        />
                                                    )}
                                                />
                                            </div>
                                        </ListItem4ByecodeUi>
                                    </>
                                )}
                            </>
                        )}
                    </Group>
                </>
            )}
            {showRelativeSetting && (
                <>
                    {relatedDataSource && (
                        <>
                            <Divider color="var(--color-gray-200)" style={{ marginBottom: 12 }} />
                            {relativeShowMode === 'input' && (
                                <Group label="选择窗">
                                    <Controller
                                        name={`${prefix}relativeSelect.showType`}
                                        render={({ field: { value, onChange } }) => {
                                            return (
                                                <SelectMode
                                                    options={VIEW_DATASOURCE_OPTIONS}
                                                    itemWidth="100%"
                                                    styles={{
                                                        image: {
                                                            padding: '20px 0px 0px 0px',
                                                            borderRadius: '8px',
                                                            width: 158,
                                                            height: 88,
                                                            outline: 'var(--color-gray-200) solid 1px',
                                                            background: 'var(--color-white)'
                                                        },
                                                        root: {
                                                            padding: '0 8px',
                                                            width: '100%',
                                                            gap: '8px',
                                                            marginTop: 8
                                                        }
                                                    }}
                                                    value={value}
                                                    onChange={val => onChange(val)}
                                                />
                                            )
                                        }}
                                    />
                                </Group>
                            )}

                            <SCxRelationSelectContainer>
                                {relativeShowMode === 'input' && (
                                    <Controller
                                        name={`${prefix}relativeSelect.viewFieldSettings`}
                                        render={({ field: { onChange, value } }) => {
                                            const currentColumns = getViewColumns({
                                                tableProps: relatedTableProps,
                                                value,
                                                schema: relatedSchema
                                            })
                                            // const currentPrimaryField = currentColumns?.[0]?.fieldId ?? ''
                                            const visibleNum = currentColumns.filter(item => item.visible).length
                                            const isAllVisible = visibleNum === currentColumns.length
                                            return (
                                                <ListItemMenu
                                                    label="字段设置"
                                                    value={isAllVisible ? '全部显示' : `显示${visibleNum}个字段`}
                                                    icon={<IconFont type="Setting" color="var(--color-gray-400)" size={16} />}
                                                    ref={btnRef}
                                                    popupContent={
                                                        <ViewFieldDisplayConfigure
                                                            columns={currentColumns}
                                                            onChange={val => {
                                                                const newValue = reduce<TableColumn, ViewField[]>(
                                                                    (preVal, curVal) => {
                                                                        const columnItem = find(
                                                                            item => item.fieldId === curVal.id,
                                                                            currentColumns
                                                                        )
                                                                        if (!columnItem) {
                                                                            return preVal
                                                                        }
                                                                        return [...preVal, { ...columnItem, visible: curVal.visible }]
                                                                    },
                                                                    [],
                                                                    val
                                                                )
                                                                onChange(newValue)
                                                            }}
                                                        />
                                                    }
                                                />
                                            )
                                        }}
                                    />
                                )}

                                <Controller
                                    name={`${prefix}relativeSelect.filter`}
                                    render={({ field: { onChange, value } }) => {
                                        const filter = value
                                        const filtersLength = filter?.expression?.conditions?.length || 0
                                        return (
                                            <ListItemMenu
                                                width="auto"
                                                label="筛选选项"
                                                value={Boolean(filtersLength) && `${String(filtersLength)} 条筛选项`}
                                                icon={<IconFont type="Filter" color="var(--color-gray-400)" size={16} />}
                                                popupContent={
                                                    <ViewFieldFilterConfigure
                                                        dataSource={relatedDataSource}
                                                        dataSourceList={dataSourceList}
                                                        availableFieldBlocksInfo={availableFieldBlocksInfo}
                                                        availableFilterBlocksInfo={availableFilterBlocksInfo}
                                                        pagePathParameter={pagePathParameter}
                                                        pageQueryParameter={pageQueryParameter}
                                                        pageVariables={pageVariables}
                                                        pageStateParameter={pageStateParameter}
                                                        viewDsId={customViewDsId}
                                                        viewType={customViewDsId ? 'custom' : undefined}
                                                        columns={columns}
                                                        paramsMode={FilterMode.CUSTOM}
                                                        primaryField={primaryField}
                                                        fieldMode={FilterMode.CUSTOM}
                                                        filter={filter}
                                                        noSettingInnerType={notDataSettingInnerTypes}
                                                        onFilter={onChange}
                                                        onMouseEnter={handleMouseEnter}
                                                        onMouseLeave={handleMouseLeave}
                                                    />
                                                }
                                            />
                                        )
                                    }}
                                />

                                <Controller
                                    name={`${prefix}relativeSelect.disabledFilter`}
                                    control={control}
                                    render={({ field: { onChange, value } }) => {
                                        const filtersLength = value?.expression?.conditions?.length || 0
                                        return (
                                            <ListItemMenu
                                                width="auto"
                                                label="禁用选项"
                                                value={Boolean(filtersLength) && `${String(filtersLength)} 条筛选项`}
                                                icon={<IconFont type="Forbidden" color="var(--color-gray-400)" size={16} />}
                                                popupContent={
                                                    <ViewFieldFilterConfigure
                                                        dataSource={relatedDataSource}
                                                        dataSourceList={dataSourceList}
                                                        availableFieldBlocksInfo={availableFieldBlocksInfo}
                                                        availableFilterBlocksInfo={availableFilterBlocksInfo}
                                                        pagePathParameter={pagePathParameter}
                                                        pageQueryParameter={pageQueryParameter}
                                                        pageStateParameter={pageStateParameter}
                                                        pageVariables={pageVariables}
                                                        columns={columns}
                                                        viewDsId={customViewDsId}
                                                        viewType={customViewDsId ? 'custom' : undefined}
                                                        paramsMode={FilterMode.CUSTOM}
                                                        primaryField={primaryField}
                                                        fieldMode={FilterMode.NORMAL}
                                                        filter={value}
                                                        noSettingInnerType={notDataSettingInnerTypes}
                                                        onFilter={onChange}
                                                        onMouseEnter={handleMouseEnter}
                                                        onMouseLeave={handleMouseLeave}
                                                    />
                                                }
                                            />
                                        )
                                    }}
                                />

                                <Controller
                                    name={`${prefix}relativeSelect.sorts`}
                                    render={({ field: { onChange, value } }) => (
                                        <ListItemMenu
                                            width="auto"
                                            label="排序数据"
                                            value={Boolean(value?.length) && `${String(value?.length)} 条排序`}
                                            icon={<IconFont type="ArrowsDownUp" color="var(--color-gray-400)" size={16} />}
                                            popupContent={
                                                <ViewSortConfigure
                                                    noSettingInnerType={notDataSettingInnerTypes}
                                                    columns={columns}
                                                    primaryField={primaryField}
                                                    sorters={value}
                                                    onChangeSorter={onChange}
                                                    isShowDsName={relatedDataSource?.type === DataSourceType.joinDataSource}
                                                />
                                            }
                                        />
                                    )}
                                />
                            </SCxRelationSelectContainer>
                            <Divider style={{ margin: '8px 0' }} />
                            <Box px={8}>
                                <Controller
                                    control={control}
                                    name={`${prefix}relativeSelect.addButton.enable`}
                                    render={({ field }) => (
                                        <SwitchControl
                                            label={<Text weight={700}>自定义动作</Text>}
                                            checked={field.value}
                                            onChange={e => field.onChange(e.target.checked)}
                                        />
                                    )}
                                />

                                {addButtonEnable && (
                                    <>
                                        <Controller
                                            control={control}
                                            name={`${prefix}relativeSelect.addButton.name`}
                                            render={({ field }) => (
                                                <InputControl
                                                    label="标题"
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    onBlur={field.onChange}
                                                />
                                            )}
                                        />

                                        <ListItem4ByecodeUi alignItems="center" justifyContent="space-between">
                                            <Text>按钮元素</Text>
                                            <SCxRightFill>
                                                <Controller
                                                    name={`${prefix}relativeSelect.addButton.showType`}
                                                    control={control}
                                                    defaultValue={showType}
                                                    render={({ field }) => (
                                                        <CM.BlockSettingsSelect
                                                            // withinPortal
                                                            searchable={false}
                                                            // rightSection={<IconFont type="ArrowDownSmall" />}
                                                            name="pointer"
                                                            defaultValue="icon-name"
                                                            value={field.value}
                                                            options={btnTypeList}
                                                            style={{
                                                                width: field.value === 'name' ? 180 : 140
                                                            }}
                                                            onChange={field.onChange}
                                                        />
                                                    )}
                                                />
                                                {showType !== 'name' && (
                                                    <Controller
                                                        name={`${prefix}relativeSelect.addButton.icon`}
                                                        control={control}
                                                        render={({ field }) => (
                                                            <IconControl
                                                                allowIconTypes={PURE_ALLOW_ICON_TYPES}
                                                                value={field.value}
                                                                onChange={field.onChange}
                                                                clearable={false}
                                                                position="top-end"
                                                            />
                                                        )}
                                                    />
                                                )}
                                            </SCxRightFill>
                                        </ListItem4ByecodeUi>
                                        {showType !== 'name' && (
                                            <Controller
                                                name={`${prefix}relativeSelect.addButton.iconPosition`}
                                                control={control}
                                                defaultValue={iconPosition}
                                                render={({ field }) => (
                                                    <FormSegmentedControl
                                                        label="图标位置"
                                                        data={ICON_POSITION_CONTROL}
                                                        value={field.value}
                                                        onChange={field.onChange}
                                                    />
                                                )}
                                            />
                                        )}
                                        <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                            <Text>图标颜色</Text>
                                            <Controller
                                                name={`${prefix}relativeSelect.addButton.iconColor`}
                                                control={control}
                                                render={({ field }) => (
                                                    <Popover
                                                        width={300}
                                                        withinPortal
                                                        position="left-start"
                                                        autoUpdate={false}
                                                        offsetOptions={152}
                                                    >
                                                        <Popover.Target>
                                                            <FilledInput
                                                                style={{ width: 180 }}
                                                                value={field.value}
                                                                onClear={() => field.onChange({ type: BACKGROUND_TYPE.color })}
                                                            />
                                                        </Popover.Target>
                                                        <FillPickerPopoverDropdown
                                                            title="填充"
                                                            value={field.value}
                                                            onChange={field.onChange}
                                                            enabledBackgroundTypes={[BACKGROUND_TYPE.color]}
                                                            {...colorSystemMethods}
                                                        />
                                                    </Popover>
                                                )}
                                            />
                                        </ListItem4ByecodeUi>
                                        <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                            <Text>文字颜色</Text>
                                            <Controller
                                                name={`${prefix}relativeSelect.addButton.color`}
                                                control={control}
                                                render={({ field }) => (
                                                    <Popover
                                                        width={300}
                                                        withinPortal
                                                        position="left-start"
                                                        autoUpdate={false}
                                                        offsetOptions={152}
                                                    >
                                                        <Popover.Target>
                                                            <FilledInput
                                                                style={{ width: 180 }}
                                                                value={field.value}
                                                                onClear={() => field.onChange({ type: BACKGROUND_TYPE.color })}
                                                            />
                                                        </Popover.Target>
                                                        <FillPickerPopoverDropdown
                                                            title="填充"
                                                            value={field.value}
                                                            onChange={field.onChange}
                                                            enabledBackgroundTypes={[BACKGROUND_TYPE.color]}
                                                            {...colorSystemMethods}
                                                        />
                                                    </Popover>
                                                )}
                                            />
                                        </ListItem4ByecodeUi>
                                        <div style={{ padding: '0 8px' }}>
                                            <Controller
                                                control={control}
                                                name={`${prefix}relativeSelect.addButton.eventList`}
                                                render={({ field }) => (
                                                    <EventList blockType="button" value={field.value} onChange={field.onChange} />
                                                )}
                                            />
                                        </div>
                                    </>
                                )}
                            </Box>
                        </>
                    )}
                </>
            )}
            {inputType === 'calendar' && (
                <>
                    <Divider color="var(--color-gray-200)" />
                    <CalendarConfig mode={mode} pointer={pointer} prefix={prefix} />
                    <Divider color="var(--color-gray-200)" />
                    <CalendarDataConfig mode={mode} pointer={pointer} prefix={prefix} />
                </>
            )}
        </>
    )
}
